import React, { useMemo, useState } from "react";
import { Button, Card, CardBody, CardHeader, Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from "@nextui-org/react";
import { PlusIcon, EyeIcon } from "@heroicons/react/24/outline";
import useSWR from "swr";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import CustomDataGrid from "../../../components/elements/CustomDataGrid";
import ControlledInput from "../../../components/elements/ControlledInput";
import { getFetcher, postFetcher, getSelectFetcher } from "../../../services/api";
import RootAdmin from "../../../components/layouts/RootAdmin";
import { useForm } from "react-hook-form";
import useAuth from "../../../hooks/useAuth";
import ControlledReactSelect from "../../../components/elements/ControlledReactSelect";
import ControlledReactQuill from "../../../components/elements/ControlledReactQuill";
import RolesAccess from "../../../enums/roles";
import { hasValidRole } from "../../../services/helpers";
import ClientDataGrid from "../../../components/elements/ClientDataGrid";

export default function TicketPage() {
  // Fetch tickets data
  const { user, roles } = useAuth();
  const [searchKeyword, setSearchKeyword] = useState("");
  const { data, isLoading, mutate } = useSWR(
    hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_KONSULTASI]) // Jika Super Admin, fetch semua konsultasi
      ? `/api/tickets?returnAll=true${searchKeyword ? `&keyword=${searchKeyword}` : ""}`
      : `/api/tickets?companyId=${user.companies.map((company) => company.companyId).join(",")}&returnAll=true${searchKeyword ? `&keyword=${searchKeyword}` : ""}`,
    getFetcher,
  );
  const navigate = useNavigate(); // Initialize useNavigate
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const { data: allCompanies } = useSWR(
    hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_KONSULTASI])
      ? `/api/company/search-company` // Jika Super Admin, fetch semua perusahaan
      : `/api/company/search-company?companyIds=${user.companies.map((company) => company.companyId).join(",")}`, // Jika bukan Super Admin, fetch perusahaan sesuai IDs
    getSelectFetcher,
  );
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm();

  const handleOpenModal = () => setIsOpenModal(true);
  const handleCloseModal = () => {
    reset();
    setIsOpenModal(false);
  };

  const handleViewTicket = (ticket) => {
    // Navigate to the ticket detail page
    navigate(`/admin/ticket/${ticket.id}`);
  };

  const handleCreateTicket = async (formData) => {
    formData.userId = user.id;
    await postFetcher("/api/tickets/create", formData);
    mutate(); // Refresh data
    handleCloseModal();
  };

  const columns = useMemo(
    () => [
      { field: "subject", headerName: "Subject", flex: 1 },
      {
        field: "companyName",
        headerName: "Nama Perusahaan",
        flex: 1,
        renderCell: (params) => <span>{params.row?.company?.name || "-"}</span>,
      },
      { field: "status", headerName: "Status", width: 150 },
      { field: "createdAt", headerName: "Created At", width: 180 },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        renderCell: (params) => (
          <div className="flex gap-2">
            <Button color="primary" isIconOnly size="sm" onPress={() => handleViewTicket(params.row)}>
              <EyeIcon className="size-4" />
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <RootAdmin>
      <Card radius="sm">
        <CardHeader>Daftar Konsultasi</CardHeader>
        <Divider />
        <CardBody>
          <div className="mb-5 flex gap-3 items-center justify-between">
            <div className="flex gap-3 items-center flex-1">
              {hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PENGELOLA]) && (
                <Button onPress={handleOpenModal} size="sm" color="primary" startContent={<PlusIcon className="size-4 stroke-2" />}>
                  Konsultasi Baru
                </Button>
              )}
              {hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_KONSULTASI]) && (
                <input
                  type="text"
                  placeholder="Cari berdasarkan subject atau nama perusahaan..."
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  className="px-3 py-2 border border-line-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent flex-1 max-w-md"
                />
              )}
            </div>
          </div>

          <div className="h-[500px]">
            <ClientDataGrid rows={data?.data || []} rowCount={data?.total || 0} isLoading={isLoading} columns={columns} />
          </div>
        </CardBody>
      </Card>

      {/* Modal for Creating a New Ticket */}
      <Modal size="lg" isOpen={isOpenModal} onOpenChange={setIsOpenModal} onClose={handleCloseModal} isDismissable={!isSubmitting}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Konsultasi Baru</ModalHeader>
              <ModalBody>
                <form onSubmit={handleSubmit(handleCreateTicket)}>
                  <div className="grid grid-cols-1 gap-3 mb-5">
                    <ControlledInput label="Subject" name="subject" control={control} isRequired placeholder="Enter ticket subject" />
                    <ControlledReactSelect control={control} name="companyId" label="Pilih Perusahaan" options={allCompanies || []} />
                    <ControlledReactQuill label="Description" name="description" control={control} isRequired placeholder="Masukan Konsultasi Anda" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button type="submit" color="primary" isLoading={isSubmitting} disabled={isSubmitting}>
                      Tambah
                    </Button>
                    <Button isDisabled={isSubmitting} color="danger" variant="faded" onPress={onClose}>
                      Batal
                    </Button>
                  </div>
                </form>
              </ModalBody>
              <ModalFooter />
            </>
          )}
        </ModalContent>
      </Modal>
    </RootAdmin>
  );
}
